import { create } from "zustand";
import type { PlainKeys } from "../lib/channel-crypto";

// Decrypted per-channel keys, scoped to the current browser tab.
//
// Storage: sessionStorage (not localStorage). The decision is
// deliberate — channel keys are sensitive material the owner shared
// out-of-band with the channel passphrase, and we don't want them
// outliving a tab close. The user pays the cost of re-entering the
// passphrase when they come back tomorrow.

const STORAGE_KEY = "playground.channel_keys";

function load(): Record<string, PlainKeys> {
  if (typeof sessionStorage === "undefined") return {};
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function persist(map: Record<string, PlainKeys>): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

type ChannelKeyState = {
  byChannel: Record<string, PlainKeys>;
  set: (channelId: string, keys: PlainKeys) => void;
  clear: (channelId: string) => void;
  get: (channelId: string) => PlainKeys | null;
};

export const useChannelKeyStore = create<ChannelKeyState>((setState, getState) => ({
  byChannel: load(),
  set: (channelId, keys) => {
    const next = { ...getState().byChannel, [channelId]: keys };
    persist(next);
    setState({ byChannel: next });
  },
  clear: (channelId) => {
    const next = { ...getState().byChannel };
    delete next[channelId];
    persist(next);
    setState({ byChannel: next });
  },
  get: (channelId) => getState().byChannel[channelId] ?? null,
}));
